import React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PageSizeSelect } from '@/components/reusable/PageSizeSelect';
import { buildPageWindow, buildSlidingPageWindow } from '@/lib/pagination/pageWindow';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

interface PaginationFooterProps {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: readonly number[];
  itemNoun?: string;
  className?: string;
  /**
   * 'anchored' (the default) keeps first and last in view: `1 … 4 5 6 … 68`.
   * 'sliding' shows three numbers that follow the user: `1 2 3`, then `3 4 5`.
   */
  pageWindow?: 'anchored' | 'sliding';
}

const navBtn =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#E4E7EC] bg-white text-[#475467] transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white';

export function PaginationFooter({
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  itemNoun = 'results',
  pageWindow = 'anchored',
  className,
}: PaginationFooterProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Everything already fits on one page — there is nothing to page through.
  if (totalPages <= 1 && totalItems <= pageSize) return null;

  const showingFrom = totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const showingTo = Math.min(currentPage * pageSize, totalItems);
  const pages =
    pageWindow === 'sliding'
      ? buildSlidingPageWindow(currentPage, totalPages)
      : buildPageWindow(currentPage, totalPages);
  const atFirst = currentPage <= 1;
  const atLast = currentPage >= totalPages;

  return (
    <div
      className={cn(
        'flex flex-col items-start justify-between gap-3 border-t border-[#F2F4F7] px-5 py-3 sm:flex-row sm:items-center',
        className,
      )}
      data-testid="pagination-footer"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-[12.5px] tabular-nums text-[#6B7280]">
          Showing <span className="font-semibold text-[#101828]">{showingFrom}</span>–
          <span className="font-semibold text-[#101828]">{showingTo}</span> of{' '}
          <span className="font-semibold text-[#101828]">{totalItems}</span> {itemNoun}
        </span>
        <label className="flex items-center gap-2 text-[12.5px] text-[#6B7280]">
          <span>Per page</span>
          <PageSizeSelect
            value={pageSize}
            options={[...pageSizeOptions]}
            onChange={onPageSizeChange}
            data-testid="pagination-page-size"
          />
        </label>
      </div>

      {totalPages > 1 ? (
        <nav
          aria-label="Pagination"
          className="flex flex-wrap items-center gap-1 sm:justify-end"
        >
          <button
            type="button"
            onClick={() => onPageChange(1)}
            disabled={atFirst}
            aria-label="First page"
            className={navBtn}
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={atFirst}
            aria-label="Previous page"
            className={navBtn}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {pages.map((p, idx) =>
            p === 'ellipsis' ? (
              <span
                key={`ellipsis-${idx}`}
                aria-hidden
                className="flex h-8 w-8 items-center justify-center text-[#98A2B3]"
              >
                <MoreHorizontal className="h-4 w-4" />
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                aria-label={`Go to page ${p}`}
                aria-current={p === currentPage ? 'page' : undefined}
                className={cn(
                  'inline-flex h-8 min-w-[2rem] shrink-0 items-center justify-center rounded-lg border px-2 text-[12.5px] font-semibold tabular-nums transition',
                  p === currentPage
                    ? 'border-brand bg-brand-soft text-brand-cta'
                    : 'border-[#E4E7EC] bg-white text-[#475467] hover:bg-slate-50',
                )}
              >
                {p}
              </button>
            ),
          )}

          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={atLast}
            aria-label="Next page"
            className={navBtn}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onPageChange(totalPages)}
            disabled={atLast}
            aria-label="Last page"
            className={navBtn}
          >
            <ChevronsRight className="h-4 w-4" />
          </button>
        </nav>
      ) : null}
    </div>
  );
}
